import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// ----------------------------------------------------------------------
// Paleta Everlee Grace (manual de marca):
//   #3B2020 Deep Mahogany | #512D2D Cocoa Brown
//   #6D4242 Dusty Rosewood | #FFE4DB Blush Nude
// Os cinzentos sao quentes para acompanhar os castanhos da marca.
// ----------------------------------------------------------------------

const GREY = {
  0: '#FFFFFF',
  100: '#FBF8F7',
  200: '#F5EFED',
  300: '#E8DEDB',
  400: '#CFC0BC',
  500: '#A08F8B',
  600: '#7A6A66',
  700: '#584B48',
  800: '#332927',
  900: '#1C1514',
};

const PRIMARY = {
  lighter: '#FFE4DB',
  light: '#6D4242',
  main: '#512D2D',
  dark: '#3B2020',
  darker: '#241313',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#FFF1EC',
  light: '#FFD0C1',
  main: '#C98F7D',
  dark: '#9C6553',
  darker: '#6D4242',
  contrastText: GREY[900],
};

const INFO = {
  lighter: '#E0F2FE',
  light: '#7DD3FC',
  main: '#0EA5E9',
  dark: '#0284C7',
  darker: '#075985',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#D1FAE5',
  light: '#6EE7B7',
  main: '#10B981',
  dark: '#059669',
  darker: '#065F46',
  contrastText: '#FFFFFF',
};

const WARNING = {
  lighter: '#FEF3C7',
  light: '#FCD34D',
  main: '#F59E0B',
  dark: '#D97706',
  darker: '#92400E',
  contrastText: GREY[900],
};

const ERROR = {
  lighter: '#FEE2E2',
  light: '#FCA5A5',
  main: '#EF4444',
  dark: '#DC2626',
  darker: '#991B1B',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

// Gradientes modernos
export const gradients = {
  primary: `linear-gradient(135deg, ${PRIMARY.light} 0%, ${PRIMARY.main} 100%)`,
  secondary: `linear-gradient(135deg, ${SECONDARY.light} 0%, ${SECONDARY.main} 100%)`,
  info: `linear-gradient(135deg, ${INFO.light} 0%, ${INFO.main} 100%)`,
  success: `linear-gradient(135deg, ${SUCCESS.light} 0%, ${SUCCESS.main} 100%)`,
  warning: `linear-gradient(135deg, ${WARNING.light} 0%, ${WARNING.main} 100%)`,
  error: `linear-gradient(135deg, ${ERROR.light} 0%, ${ERROR.main} 100%)`,
  glass: `linear-gradient(135deg, ${alpha('#FFFFFF', 0.1)} 0%, ${alpha('#FFFFFF', 0.05)} 100%)`,
};

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[700],
      disabled: GREY[600],
    },
    background: {
      paper: '#FFFFFF',
      default: GREY[100],
      neutral: GREY[200],
    },
    action: {
      ...COMMON.action,
      active: GREY[700],
    },
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      // Branco sobre superfícies quase pretas; marca fica só em acentos
      primary: '#FFFFFF',
      secondary: alpha('#FFFFFF', 0.92),
      disabled: alpha('#FFFFFF', 0.48),
    },
    background: {
      // Hierarquia: canvas = sidebar → paper elevado → neutral (headers)
      // Cocoa Brown / Deep Mahogany NÃO são fills — só CTAs e destaques
      paper: '#161010',
      default: '#0A0606',
      neutral: GREY[900],
    },
    action: {
      ...COMMON.action,
      active: '#FFFFFF',
      hover: alpha('#FFFFFF', 0.12),
      selected: alpha('#FFFFFF', 0.18),
      disabled: alpha('#FFFFFF', 0.4),
      disabledBackground: alpha('#FFFFFF', 0.12),
      focus: alpha('#FFFFFF', 0.24),
    },
    divider: alpha('#FFFFFF', 0.2),
  };

  return mode === 'light' ? light : dark;
}
